import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { getPartnerAuthHeaders } from "@/lib/apiAuth";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const extractErrorMessage = (err, fallback) => {
  if (err?.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  }
  if (err?.response?.data?.message) return err.response.data.message;
  if (err?.request && !err?.response) {
    return "Network error. Please check your connection and try again.";
  }
  return fallback;
};

const COPY = {
  resume: {
    title: "Resume billing",
    body: (
      <>
        Resume billing for this client?{" "}
        <strong className="text-emerald-100">
          Wholesale billing for this slot resumes on your next invoice.
        </strong>{" "}
        No additional charge for the current billing period. The Digital Assistant keeps running.
      </>
    ),
    submit: "Resume billing",
    submitting: "Resuming…",
    success: "Billing resumed",
    endpoint: (id) => `${BACKEND_URL}/api/partner/clients/${id}/resume-billing`,
    fallbackError: "Could not resume billing. Please try again.",
    testId: "resume-billing-modal",
  },
  reactivate: {
    title: "Reactivate",
    body: (
      <>
        Reactivate this client?{" "}
        <strong className="text-emerald-100">Wholesale billing resumes immediately</strong>{" "}
        (prorated charge may apply). Access is restored. Client data and settings were preserved.
      </>
    ),
    submit: "Reactivate",
    submitting: "Reactivating…",
    success: "Client reactivated",
    endpoint: (id) => `${BACKEND_URL}/api/partner/clients/${id}/reactivate`,
    fallbackError: "Could not reactivate client. Please try again.",
    testId: "reactivate-modal",
  },
};

const ResumeBillingModal = ({ client, mode, open, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [billingLink, setBillingLink] = useState(false);

  useEffect(() => {
    if (open && client) {
      setSubmitError("");
      setBillingLink(false);
    }
  }, [open, client, mode]);

  if (!open || !client || !mode) return null;

  const copy = COPY[mode];

  const getAuthHeader = () => getPartnerAuthHeaders();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError("");
    setBillingLink(false);

    try {
      const res = await axios.post(copy.endpoint(client.id), {}, { headers: getAuthHeader() });
      toast.success(copy.success);
      onSuccess?.(res.data);
      onClose();
    } catch (err) {
      if (err?.response?.status === 401) {
        Cookies.remove("token");
        Cookies.remove("email");
        navigate("/login");
        return;
      }
      const message = extractErrorMessage(err, copy.fallbackError);
      setSubmitError(message);
      if (err?.response?.status === 402) {
        setBillingLink(true);
      }
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      data-testid={copy.testId}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-emerald-500/30 bg-[#11151F] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 sm:p-6 border-b border-partner">
          <div>
            <h2 className="text-lg font-bold text-partner">{copy.title}</h2>
            <p className="text-sm text-partner-muted font-body mt-1">{client.name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-partner-muted hover:text-partner hover:bg-white/5"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-5">
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3">
            <p className="text-sm font-body text-emerald-100/90">{copy.body}</p>
          </div>

          {submitError && (
            <div className="space-y-2">
              <p className="text-sm text-red-400 font-body" data-testid="restore-billing-error">
                {submitError}
              </p>
              {billingLink && (
                <Link
                  to="/partner/billing"
                  className="text-sm font-semibold text-partner-accent hover:underline"
                >
                  Go to Partner Billing
                </Link>
              )}
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 rounded-lg border border-partner text-partner-muted font-body text-sm hover:text-partner hover:bg-[#080A0F] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 font-semibold text-sm hover:bg-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="restore-billing-submit"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isSubmitting ? copy.submitting : copy.submit}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResumeBillingModal;
